import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useEscapeKey } from './hooks';
import { renderValue, processFormValue, getDefaultFormValue } from './utils';
import {
  AttributeInputBase,
  DynamicAttributeFormValue,
} from 'packages/common-frontend/components/AttributeInput/AttributeInputBase';
import { Attribute } from 'packages/common-frontend/components/AttributeInput/types';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';

type AttributeInputProps<T extends Attribute = Attribute> = {
  attribute: T;
  onUpdate: (updatedAttribute: T) => void;
  onOpenDrawer?: (attribute: T) => void;
};

type FormData = {
  value: DynamicAttributeFormValue;
};

export const AttributeInput = <T extends Attribute>({
  attribute,
  onUpdate,
  onOpenDrawer,
}: AttributeInputProps<T>) => {
  const [isEditing, setIsEditing] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      value: getDefaultFormValue(attribute),
    },
  });

  useEffect(() => {
    if (isEditing) {
      form.setFocus('value');
    }
  }, [form, isEditing]);

  const handleSave = ({ value }: { value: DynamicAttributeFormValue }) => {
    const updatedValue = processFormValue(value, attribute.type);

    onUpdate({
      ...attribute,
      value: updatedValue,
    });

    setIsEditing(false);
  };

  const handleCancel = () => {
    form.reset({ value: getDefaultFormValue(attribute) });
    setIsEditing(false);
  };

  useEscapeKey(handleCancel, isEditing);

  return (
    <Card
      padding="1rem"
      variant="outline"
      sx={{
        '& .hover-visible': {
          visibility: 'hidden',
        },
        '&:hover .hover-visible': {
          visibility: 'visible',
        },
      }}
    >
      <form onSubmit={form.handleSubmit(handleSave)}>
        <CardHeader px="0" pt="0" pb="0.8rem">
          <Flex align="center" justify="space-between" gap="1rem">
            <Typography variant="base" fontWeight="bold">
              {attribute.displayName}
            </Typography>
            {isEditing ? (
              <Flex gap="0.2rem">
                <IconButtonV2
                  icon={<Icon icon="close" />}
                  aria-label="Cancel"
                  variant="v2-ghost"
                  onClick={handleCancel}
                />
                <IconButtonV2
                  key="save-button" // Need a key here to prevent a form submission when user clicks on the edit button
                  icon={<Icon icon="save" />}
                  aria-label="Save"
                  variant="v2-ghost"
                  colorScheme="blue"
                  IconButtonProps={{ type: 'submit' }}
                />
              </Flex>
            ) : (
              <Flex gap="0.2rem" className="hover-visible">
                {onOpenDrawer && (
                  <IconButtonV2
                    icon={<Icon icon="clock-rotate-left" />}
                    aria-label="View History"
                    variant="v2-ghost"
                  />
                )}
                <IconButtonV2
                  icon={<Icon icon="edit" />}
                  aria-label="Edit"
                  variant="v2-ghost"
                  onClick={() => {
                    setIsEditing(true);
                  }}
                />
              </Flex>
            )}
          </Flex>
        </CardHeader>
        <Divider />
        <CardBody px="0" pb="0" pt="0.8rem">
          {isEditing ? (
            <FormProvider {...form}>
              <AttributeInputBase<FormData>
                attribute={attribute}
                name="value"
              />
            </FormProvider>
          ) : (
            <Box maxHeight="4rem" overflowY="auto">
              <Typography
                variant="base-muted"
                TextProps={{ whiteSpace: 'pre-line' }}
              >
                {renderValue(attribute)}
              </Typography>
            </Box>
          )}
        </CardBody>
      </form>
    </Card>
  );
};
